import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const SLIDES = [
  {
    label: "AfroAm",
    sub: "Basketball Association",
    accent: "text-amber-400",
    image: "/brand/logo.png",
    bg: "from-black via-[#0a0a0a] to-black",
    mode: "logo",
  },
  {
    label: "Programs",
    sub: "Elite Competition · Mentorship · Culture",
    accent: "text-red-500",
    image: "/posters/team-unity.png",
    bg: "from-red-950/40 via-black to-black",
  },
  {
    label: "Community",
    sub: "The Front Office",
    accent: "text-amber-400",
    image: "/staff/michael-lewis.png",
    bg: "from-amber-950/30 via-black to-black",
  },
  {
    label: "Showcase",
    sub: "Houston · 2027",
    accent: "text-green-500",
    image: "/brand/event-flyer.png",
    bg: "from-green-950/30 via-black to-black",
  },
  {
    label: "Voices",
    sub: "In their own words",
    accent: "text-red-500",
    image: "/posters/felice-quote.png",
    bg: "from-black via-red-950/20 to-black",
  },
  {
    label: "Store",
    sub: "Wear the movement",
    accent: "text-amber-400",
    image: "/store/athletic-set.jpg",
    bg: "from-black via-[#0a0a0a] to-amber-950/30",
  },
  {
    label: "One Family",
    sub: "Cultural Unity",
    accent: "text-green-500",
    image: "/posters/team-unity.png",
    bg: "from-green-950/40 via-black to-red-950/30",
    final: true,
  },
];

const SLIDE_DURATION = 480; // ms per slide
const TOTAL = SLIDES.length * SLIDE_DURATION;

export const IntroReel = ({ onDone }) => {
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) return;
    if (index >= SLIDES.length - 1) {
      const t = setTimeout(() => {
        setDone(true);
        setTimeout(onDone, 450);
      }, SLIDE_DURATION + 350);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setIndex((i) => i + 1), SLIDE_DURATION);
    return () => clearTimeout(t);
  }, [index, done, onDone]);

  const handleSkip = () => {
    setDone(true);
    setTimeout(onDone, 250);
  };

  const slide = SLIDES[index];

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="intro-reel"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45 }}
          data-testid="intro-reel"
          className="fixed inset-0 z-[100] overflow-hidden bg-black"
        >
          {/* Slide background */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`slide-${index}`}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.32, ease: "easeOut" }}
              className={`absolute inset-0 bg-gradient-to-br ${slide.bg}`}
            >
              {/* Image */}
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src={slide.image}
                  alt={slide.label}
                  className={`${
                    slide.mode === "logo"
                      ? "max-h-[50vh] w-auto object-contain"
                      : "w-full h-full object-cover"
                  } opacity-25`}
                />
              </div>
              <div className="absolute inset-0 bg-black/40" />
              {/* Grain */}
              <div
                className="absolute inset-0 pointer-events-none opacity-[0.08]"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                }}
              />
            </motion.div>
          </AnimatePresence>

          {/* Centered typography */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={`text-${index}`}
                initial={{ opacity: 0, y: 30, letterSpacing: "0.2em" }}
                animate={{ opacity: 1, y: 0, letterSpacing: "-0.02em" }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.38, ease: "easeOut" }}
                className="text-center"
              >
                <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-400 mb-4">
                  {String(index + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
                </div>
                <h1
                  className={`font-display uppercase text-[14vw] md:text-[10vw] leading-[0.85] ${slide.accent}`}
                  style={{ textShadow: "0 0 60px rgba(0,0,0,0.6)" }}
                >
                  {slide.label}
                </h1>
                <div className="text-sm md:text-base uppercase tracking-[0.4em] text-neutral-300 mt-6">
                  {slide.sub}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Final slide CTA hint */}
            {slide.final && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.4 }}
                className="absolute bottom-16 text-[10px] uppercase tracking-[0.4em] text-amber-400 flex items-center gap-3"
              >
                <span className="w-8 h-px bg-amber-400" />
                Welcome to the family
                <span className="w-8 h-px bg-amber-400" />
              </motion.div>
            )}
          </div>

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-neutral-900 z-20">
            <motion.div
              key={`progress-${index}`}
              className="h-full bg-amber-400 origin-left"
              initial={{ scaleX: index / SLIDES.length }}
              animate={{ scaleX: (index + 1) / SLIDES.length }}
              transition={{ duration: SLIDE_DURATION / 1000, ease: "linear" }}
              style={{ width: "100%" }}
            />
          </div>

          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 md:px-12 py-5">
            <div className="flex items-center gap-3">
              <img
                src="/brand/logo.png"
                alt="AfroAm"
                className="h-9 w-auto object-contain"
              />
              <div className="hidden sm:block text-[10px] uppercase tracking-[0.4em] text-neutral-400">
                AfroAm Basketball Association
              </div>
            </div>
            <button
              onClick={handleSkip}
              data-testid="intro-skip"
              className="group inline-flex items-center gap-2 border border-neutral-700 hover:border-white text-neutral-400 hover:text-white px-4 py-2 text-[10px] uppercase tracking-[0.35em] transition-colors"
              aria-label="Skip intro"
            >
              <X size={12} />
              Skip
            </button>
          </div>

          {/* Side ornaments */}
          <div className="absolute left-6 md:left-12 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.5em] text-neutral-600 rotate-180 [writing-mode:vertical-rl]">
            One Game · Two Cultures
          </div>
          <div className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.5em] text-neutral-600 [writing-mode:vertical-rl]">
            Las Vegas · Houston · San Francisco
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { TOTAL as INTRO_DURATION };
